import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import ProfawzLogo from "../components/ProfawzLogo";
import { User, Phone, Mail, Video, Calendar, FileText, CheckCircle2, ArrowRight, Loader2, LogIn, Building2, Copy, Check, MessageSquare, Send, Star } from "lucide-react";
import { toast } from "sonner";

export function TutorLogin() {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        const clean = code.trim().toUpperCase();
        if (!clean.startsWith("TUT-") || clean.length < 8) {
            setError("Le code tuteur commence par TUT- suivi de 6 caractères.");
            return;
        }
        setLoading(true);
        try {
            await api.get(`/public/tutor/space/${clean}`);
            navigate(`/tuteur/${clean}`);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Code invalide");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <header className="border-b border-rule bg-white">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
                    <Link to="/" className="flex items-center gap-3">
                        <ProfawzLogo size={28} />
                        <div>
                            <div className="h-display text-base font-bold">Profawz</div>
                            <div className="text-[10px] font-mono text-muted2 uppercase tracking-wider">Espace tuteur entreprise</div>
                        </div>
                    </Link>
                </div>
            </header>
            <main className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <div className="label-eyebrow mb-2">Connexion tuteur</div>
                    <h1 className="h-display text-3xl sm:text-4xl font-semibold mb-3">
                        Entrez le <span className="text-orange">code tuteur</span> de votre alternant
                    </h1>
                    <p className="text-sm text-muted2 mb-6">
                        Votre alternant vous a transmis un code commençant par <span className="font-mono font-semibold text-ink">TUT-</span>. Saisissez-le ci-dessous pour accéder à son espace : bilans à évaluer, prise de RDV, contacts.
                    </p>

                    <form onSubmit={submit} className="space-y-4" data-testid="tutor-login-form">
                        <div>
                            <label className="label-eyebrow mb-2 block">Code tuteur</label>
                            <input
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                className="field-input font-mono text-lg text-center tracking-wider uppercase"
                                placeholder="TUT-XXXXXX"
                                required
                                autoComplete="off"
                                data-testid="tutor-login-code-input"
                            />
                        </div>

                        {error && (
                            <div className="text-sm text-destructive border border-destructive/20 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="tutor-login-error">
                                {error}
                            </div>
                        )}

                        <button type="submit" disabled={loading} className="btn-primary w-full !py-3" data-testid="tutor-login-submit">
                            {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                            {loading ? "Vérification…" : "Accéder à l'espace tuteur"}
                        </button>
                        <p className="text-[11px] text-muted2 text-center">
                            Aucun compte à créer. L'accès expire si votre alternant régénère son code.
                        </p>
                    </form>
                </div>
            </main>
        </div>
    );
}

export function TutorSpace() {
    const { code } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = async () => {
        try {
            const { data } = await api.get(`/public/tutor/space/${code}`);
            setData(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Code invalide");
            navigate("/tuteur");
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [code]);

    if (loading) return <div className="min-h-screen bg-paper flex items-center justify-center text-muted2">Chargement…</div>;
    if (!data) return null;

    const { alternant, bilans, responsable, formateurs = [] } = data;
    const fullName = `${alternant.prenom || ""} ${alternant.nom || ""}`.trim() || alternant.email;
    const pendingBilans = bilans.filter((b) => !b.evaluated);
    const evaluatedBilans = bilans.filter((b) => b.evaluated);

    return (
        <div className="min-h-screen bg-paper">
            <header className="border-b border-rule bg-white sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-3">
                        <ProfawzLogo size={28} />
                        <div>
                            <div className="h-display text-base font-bold">Profawz</div>
                            <div className="text-[10px] font-mono text-muted2 uppercase tracking-wider">Espace tuteur</div>
                        </div>
                    </Link>
                    <Link to="/tuteur" className="text-xs font-mono text-muted2 hover:text-ink" data-testid="tutor-space-logout">
                        Déconnexion
                    </Link>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="tutor-space-page">
                {/* HERO */}
                <div className="panel p-6 mb-8 bg-gradient-to-br from-emerald-50 to-white border-emerald-200" data-testid="tutor-hero">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                        <div className="w-16 h-16 bg-emerald-500 text-white flex items-center justify-center h-display text-2xl font-bold shrink-0">
                            {(alternant.prenom?.[0] || "A").toUpperCase()}{(alternant.nom?.[0] || "").toUpperCase()}
                        </div>
                        <div className="flex-1">
                            <div className="label-eyebrow text-emerald-700 mb-1">Votre alternant</div>
                            <h1 className="h-display text-3xl font-semibold leading-tight">{fullName}</h1>
                            <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                <a href={`mailto:${alternant.email}`} className="inline-flex items-center gap-1.5 hover:text-orange" data-testid="tutor-alternant-email">
                                    <Mail size={12} /> {alternant.email}
                                </a>
                                {alternant.phone && (
                                    <a href={`tel:${alternant.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-orange" data-testid="tutor-alternant-phone">
                                        <Phone size={12} /> {alternant.phone}
                                    </a>
                                )}
                                {alternant.nom_etablissement && (
                                    <span className="inline-flex items-center gap-1.5 text-muted2">
                                        <Building2 size={12} /> {alternant.nom_etablissement}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3 COLUMNS */}
                <div className="grid lg:grid-cols-3 gap-4">
                    {/* BILANS */}
                    <div className="lg:col-span-2 space-y-4">
                        <BilansSection title="Bilans à évaluer" bilans={pendingBilans} accent="orange" emptyMsg={bilans.length === 0 ? "L'alternant n'a pas encore rédigé de bilan." : "Tous les bilans ont été évalués. Merci !"} />
                        {evaluatedBilans.length > 0 && (
                            <BilansSection title="Bilans déjà évalués" bilans={evaluatedBilans} accent="emerald" emptyMsg="" />
                        )}
                        {/* Standalone evaluation — always available, even when no bilan exists */}
                        <div className="panel p-5 border-2 border-dashed border-orange/40 bg-orange/5" data-testid="tutor-standalone-card">
                            <div className="flex items-start gap-3">
                                <Star size={18} className="text-orange mt-0.5 shrink-0" />
                                <div className="flex-1">
                                    <div className="label-eyebrow mb-1">Évaluation libre</div>
                                    <h3 className="h-display text-base font-semibold">Évaluer l'alternant sans bilan</h3>
                                    <p className="text-xs text-muted2 mt-1 leading-relaxed">
                                        {bilans.length === 0
                                            ? `${alternant.prenom} n'a pas encore rédigé de bilan, mais vous pouvez tout de même lui transmettre votre évaluation à l'établissement.`
                                            : "Vous pouvez aussi déposer une évaluation indépendante des bilans — utile pour un point d'étape ponctuel."}
                                    </p>
                                    <Link to={`/tuteur/${code}/evaluer`} className="btn-primary !text-xs !px-4 !py-2 mt-3 inline-flex" data-testid="tutor-standalone-link">
                                        <Star size={12} /> Faire une évaluation
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CONTACT RESPONSABLE + RDV */}
                    <div>
                        {responsable ? (
                            <div className="panel p-5 bg-ink text-white border-ink" data-testid="tutor-responsable-card">
                                <div className="label-eyebrow text-orange/90 mb-1">Responsable de formation</div>
                                <h2 className="h-display text-xl font-semibold leading-tight">{responsable.name}</h2>
                                <div className="text-xs text-white/70 mt-1">{responsable.nom_etablissement}</div>

                                <div className="mt-5 space-y-2">
                                    {responsable.phone && (
                                        <a href={`tel:${responsable.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-sm text-white/85 hover:text-white" data-testid="tutor-resp-phone">
                                            <Phone size={14} className="text-orange" /> {responsable.phone}
                                        </a>
                                    )}
                                    <a href={`mailto:${responsable.email}`} className="flex items-center gap-2 text-sm text-white/85 hover:text-white" data-testid="tutor-resp-email">
                                        <Mail size={14} className="text-orange" /> {responsable.email}
                                    </a>
                                </div>

                                <div className="mt-5 pt-5 border-t border-white/15">
                                    <div className="label-eyebrow text-orange/90 mb-2">Prendre rendez-vous</div>
                                    <Link
                                        to={`/rdv/${responsable.invite_code}`}
                                        className="btn-primary w-full !justify-center"
                                        data-testid="tutor-book-rdv-button"
                                    >
                                        <Calendar size={14} /> Réserver un créneau
                                    </Link>
                                    <p className="text-[11px] text-white/60 mt-2">
                                        Le responsable propose ses créneaux. Vous pouvez laisser un lien visio (Meet, Teams, Zoom) ou demander un appel téléphonique.
                                    </p>
                                </div>

                                <div className="mt-4 pt-4 border-t border-white/15 grid grid-cols-2 gap-2">
                                    {responsable.phone && (
                                        <a href={`tel:${responsable.phone.replace(/\s/g, "")}`} className="btn-outline !bg-transparent !text-white !border-white/30 hover:!bg-white/10 !text-xs !py-2 !px-2" data-testid="tutor-call-button">
                                            <Phone size={12} /> Appeler
                                        </a>
                                    )}
                                    <a
                                        href={`https://meet.jit.si/Profawz-${encodeURIComponent(fullName)}-${Date.now()}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-outline !bg-transparent !text-white !border-white/30 hover:!bg-white/10 !text-xs !py-2 !px-2"
                                        data-testid="tutor-visio-button"
                                    >
                                        <Video size={12} /> Démarrer visio
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <div className="panel p-5 text-sm text-muted2">Aucun responsable de formation n'est associé à cet établissement pour le moment.</div>
                        )}

                        {/* TIPS */}
                        <div className="panel p-4 mt-4 bg-orange/5 border-orange/20">
                            <div className="label-eyebrow text-orange mb-2">À savoir</div>
                            <ul className="space-y-1.5 text-xs text-ink/75 leading-relaxed">
                                <li>✓ Vos réponses sont transmises directement au responsable de formation.</li>
                                <li>✓ Le lien visio généré utilise Jitsi Meet (gratuit, sans inscription).</li>
                                <li>✓ Vous pouvez revenir à tout moment avec votre code tuteur <span className="font-mono font-semibold">{code}</span>.</li>
                            </ul>
                        </div>

                        {/* MESSAGE RESPONSABLE / FORMATEURS / ALTERNANT */}
                        {responsable && (
                            <TutorMessageCenter
                                code={code}
                                responsable={responsable}
                                alternant={alternant}
                                formateurs={formateurs}
                                fullName={fullName}
                            />
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function BilansSection({ title, bilans, accent, emptyMsg }) {
    if (!bilans || bilans.length === 0) {
        if (!emptyMsg) return null;
        return (
            <div className="panel p-6 text-sm text-muted2 text-center" data-testid="tutor-bilans-empty">
                <CheckCircle2 size={28} className="text-emerald-500 mx-auto mb-2" />
                {emptyMsg}
            </div>
        );
    }
    const accentClasses = {
        orange: "border-l-orange",
        emerald: "border-l-emerald-500",
    };
    return (
        <div>
            <h2 className="h-display text-xl font-semibold mb-3 flex items-center gap-2">
                <FileText size={18} className="text-orange" /> {title} <span className="text-xs font-mono text-muted2 font-normal">({bilans.length})</span>
            </h2>
            <div className="space-y-2">
                {bilans.map((b) => (
                    <Link
                        key={b.id}
                        to={`/tuteur/${b.id}/${b.tutor_token}`}
                        className={`panel p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 group hover:border-ink transition-colors border-l-4 ${accentClasses[accent]}`}
                        data-testid={`tutor-bilan-${b.id}`}
                    >
                        <div>
                            <div className="font-semibold">{b.entreprise || "Entreprise non renseignée"}</div>
                            <div className="text-xs font-mono text-muted2 mt-0.5">
                                {b.date_debut} → {b.date_fin} · {b.niveau === "1ere" ? "1ère année" : "2ème année"}
                            </div>
                            {b.tuteur && <div className="text-xs text-muted2 mt-0.5">Tuteur indiqué : {b.tuteur}</div>}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                            {b.evaluated ? (
                                <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center"><CheckCircle2 size={11} className="mr-1" /> Évalué</span>
                            ) : (
                                <span className="pill bg-orange-50 text-orange-700 border border-orange-200 inline-flex items-center">À évaluer</span>
                            )}
                            <ArrowRight size={14} className="text-orange opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

// Shown on alternant dashboard: a card displaying their tutor code + copy button
export function TutorCodeCard({ tutorCode }) {
    const [copied, setCopied] = useState(false);
    if (!tutorCode) return null;
    const tutorUrl = `${window.location.origin}/tuteur`;
    const shareMessage = `Bonjour, voici mon code d'accès à Profawz pour que vous puissiez évaluer mes bilans d'alternance et prendre rendez-vous avec mon responsable de formation :\n\nCode : ${tutorCode}\nLien : ${tutorUrl}\n\nMerci !`;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(tutorCode);
            setCopied(true);
            toast.success("Code tuteur copié !");
            setTimeout(() => setCopied(false), 2200);
        } catch { toast.error("Impossible de copier"); }
    };
    const smsHref = `sms:?body=${encodeURIComponent(shareMessage)}`;
    const mailHref = `mailto:?subject=${encodeURIComponent("Mon code Profawz — accès tuteur")}&body=${encodeURIComponent(shareMessage)}`;
    const waHref = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    return (
        <div className="panel p-5 bg-ink text-white border-ink" data-testid="tutor-code-card">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                    <div className="label-eyebrow text-orange/90 mb-1">Code tuteur</div>
                    <div className="h-display text-3xl font-bold font-mono tracking-wider" data-testid="tutor-code-value">{tutorCode}</div>
                    <p className="text-xs text-white/70 mt-2 max-w-md leading-relaxed">
                        Partagez ce code à votre tuteur entreprise. Il se connecte sur <span className="font-mono text-orange">{tutorUrl.replace(/^https?:\/\//, "")}</span> pour accéder à son espace : évaluer vos bilans + prendre RDV avec votre responsable.
                    </p>
                </div>
                <button onClick={copy} className="btn-outline !bg-transparent !text-white !border-white/30 hover:!bg-white/10 !text-xs self-start shrink-0" data-testid="tutor-code-copy-button">
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? "Copié" : "Copier le code"}
                </button>
            </div>
            <div className="mt-4 pt-4 border-t border-white/15">
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">Envoyer à mon tuteur en 1 clic</div>
                <div className="grid grid-cols-3 gap-2">
                    <a href={smsHref} className="btn-outline !bg-transparent !text-white !border-white/30 hover:!bg-white/10 !text-xs !py-2 !justify-center" data-testid="tutor-code-share-sms">
                        <MessageSquare size={12} /> SMS
                    </a>
                    <a href={mailHref} className="btn-outline !bg-transparent !text-white !border-white/30 hover:!bg-white/10 !text-xs !py-2 !justify-center" data-testid="tutor-code-share-email">
                        <Mail size={12} /> E-mail
                    </a>
                    <a href={waHref} target="_blank" rel="noreferrer" className="btn-outline !bg-transparent !text-white !border-white/30 hover:!bg-white/10 !text-xs !py-2 !justify-center" data-testid="tutor-code-share-whatsapp">
                        <Send size={12} /> WhatsApp
                    </a>
                </div>
            </div>
        </div>
    );
}

export default TutorSpace;

function TutorMessageCenter({ code, responsable, alternant, formateurs, fullName }) {
    // Build the list of available recipients
    const recipients = useMemo(() => {
        const list = [{
            id: "",  // empty → backend defaults to responsable
            label: responsable.name || "Responsable",
            sublabel: "Responsable de formation",
            kind: "responsable",
        }];
        if (alternant?.id) {
            list.push({
                id: alternant.id,
                label: fullName,
                sublabel: "Votre alternant",
                kind: "alternant",
            });
        }
        (formateurs || []).forEach((f) => {
            list.push({
                id: f.id,
                label: f.name,
                sublabel: "Formateur (a évalué votre alternant)",
                kind: "formateur",
            });
        });
        return list;
    }, [responsable, alternant, formateurs, fullName]);

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(recipients[0]);
    const [thread, setThread] = useState(null);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    const load = async () => {
        try {
            const params = selected.id ? `?recipient_id=${encodeURIComponent(selected.id)}` : "";
            const { data } = await api.get(`/tutor-chat/${code}/messages${params}`);
            setThread(data);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        if (!open) return;
        load();
        const t = setInterval(load, 12000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [open, code, selected.id]);

    const send = async (e) => {
        e?.preventDefault?.();
        if (!body.trim()) return;
        setSending(true);
        try {
            const payload = { body: body.trim() };
            if (selected.id) payload.recipient_id = selected.id;
            await api.post(`/tutor-chat/${code}/messages`, payload);
            setBody("");
            await load();
            toast.success(`Message envoyé à ${selected.label}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'envoi.");
        } finally {
            setSending(false);
        }
    };

    if (!open) {
        const parts = ["responsable"];
        if (alternant?.id) parts.push("alternant");
        if (formateurs?.length) parts.push(formateurs.length > 1 ? "formateurs" : "formateur");
        const totalCount = recipients.length;
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="panel p-4 mt-4 w-full text-left hover:border-orange transition-colors flex items-center gap-3"
                data-testid="tutor-open-message-button"
            >
                <div className="w-10 h-10 bg-orange/15 flex items-center justify-center shrink-0">
                    <MessageSquare size={18} className="text-orange" />
                </div>
                <div className="flex-1">
                    <div className="text-sm font-semibold">Envoyer un message</div>
                    <div className="text-[11px] text-muted2 mt-0.5">
                        {totalCount > 1
                            ? `${totalCount} destinataires · ${parts.join(", ")}`
                            : `${responsable.name} · ${responsable.nom_etablissement}`}
                    </div>
                </div>
                <ArrowRight size={14} className="text-orange" />
            </button>
        );
    }

    const messages = thread?.messages || [];

    return (
        <div className="panel p-0 mt-4 overflow-hidden" data-testid="tutor-message-pane">
            <div className="px-4 py-3 border-b border-rule flex items-center gap-3 bg-ink text-white">
                <div className="w-9 h-9 bg-orange/20 flex items-center justify-center shrink-0">
                    <MessageSquare size={16} className="text-orange" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">Échange avec {selected.label}</div>
                    <div className="text-[10px] font-mono text-white/60 mt-0.5">{selected.sublabel}</div>
                </div>
                <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white text-xl leading-none px-1" aria-label="Fermer" data-testid="tutor-message-close">
                    ×
                </button>
            </div>

            {/* RECIPIENT SELECTOR */}
            {recipients.length > 1 && (
                <div className="px-3 py-2 border-b border-rule bg-paper" data-testid="tutor-recipient-selector">
                    <label className="label-eyebrow text-[9px] mb-1 block">Destinataire</label>
                    <select
                        value={selected.id || ""}
                        onChange={(e) => {
                            const r = recipients.find((x) => x.id === e.target.value) || recipients[0];
                            setSelected(r);
                            setThread(null);
                        }}
                        className="field-input !text-xs !py-1.5"
                        data-testid="tutor-recipient-select"
                    >
                        {recipients.map((r) => (
                            <option key={r.id || "default"} value={r.id || ""}>
                                {r.label} — {r.sublabel}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="max-h-72 overflow-y-auto p-4 space-y-2 bg-surface" data-testid="tutor-message-list">
                {messages.length === 0 ? (
                    <p className="text-center text-xs text-muted2 font-mono py-4">Aucun message. Écrivez le premier !</p>
                ) : messages.map((m) => {
                    const fromMe = m.from === "tutor";
                    const isBulletin = m.kind === "bulletin";
                    return (
                        <div key={m.id} className={`flex ${fromMe ? "justify-end" : "justify-start"}`} data-testid={`tutor-msg-${m.id}`}>
                            <div className={`max-w-[88%] px-3 py-2 ${isBulletin ? "border-2 border-orange bg-orange/5 text-ink" : (fromMe ? "bg-orange text-white" : "bg-paper border border-rule")}`}>
                                {isBulletin && (
                                    <div className="flex items-center gap-1.5 mb-1.5 text-[11px] uppercase tracking-wider font-bold text-orange">
                                        📊 Bulletin officiel
                                    </div>
                                )}
                                <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                                {isBulletin && m.attachment_path && (
                                    <a
                                        href={`${window.location.origin}/api/files/${m.attachment_path}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-ink text-white text-xs font-medium hover:bg-ink/80 transition-colors"
                                        data-testid={`tutor-bulletin-download-${m.id}`}
                                    >
                                        📎 Télécharger {m.attachment_filename || "le bulletin PDF"}
                                    </a>
                                )}
                                <div className={`text-[10px] mt-1 font-mono ${isBulletin ? "text-muted2" : (fromMe ? "text-white/70" : "text-muted2")}`}>
                                    {new Date(m.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <form onSubmit={send} className="p-3 border-t border-rule flex items-end gap-2 bg-paper" data-testid="tutor-message-form">
                <textarea
                    rows={2}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                    placeholder={`Message à ${selected.label}…`}
                    className="field-input flex-1 resize-none !py-2 !text-sm"
                    data-testid="tutor-message-input"
                />
                <button type="submit" disabled={!body.trim() || sending} className="btn-primary !py-3" data-testid="tutor-message-send-button">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
            </form>
        </div>
    );
}
